import React, { useState, useMemo, useEffect, useCallback } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { getYearOptions, exportToCsv } from '../../utils/registryExport'
import {
  MagnifyingGlassIcon,
  ChevronRightIcon,
  ClockIcon,
  PlusIcon,
  BuildingOffice2Icon,
  GlobeAltIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline'
import { Button } from '../../components/ui/button'
import PatronScreeningToolModal from './components/PatronScreeningToolModal'
import { ECDD_STATUS_CONFIG, ECDD_TRIGGER_CONFIG, ECDD_RESOLUTION_CONFIG } from '../../data/ecddData'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { isPatronScreeningPrototypeEnabled } from '../../config/featureFlags'

function useVenues() {
  const [venues, setVenues] = useState([])
  useEffect(() => {
    ;(async () => {
      try {
        const r = await authFetch(getLocalApiUrl('/api/venues'))
        const j = await r.json()
        if (j.success && Array.isArray(j.data) && j.data.length > 0) {
          setVenues(
            j.data.map((v) => ({
              id: (v.venue_id || '').toLowerCase(),
              name: v.name,
              shortName: v.code || v.name?.split(' ')[0] || v.venue_id,
            }))
          )
        }
      } catch {}
    })()
  }, [])
  return venues
}

function Badge({ bg, color, border, children }) {
  return (
    <span
      className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap"
      style={{ backgroundColor: bg, color, border: border ? `1px solid ${border}` : undefined }}
    >
      {children}
    </span>
  )
}

function StatusBadge({ status }) {
  const config = ECDD_STATUS_CONFIG[status] || ECDD_STATUS_CONFIG.open
  return <Badge bg={config.bg} color={config.color} border={config.border}>{config.label}</Badge>
}

function RiskDot({ risk }) {
  const color = risk >= 70 ? '#dc2626' : risk >= 40 ? '#ea580c' : risk >= 20 ? '#ca8a04' : '#16a34a'
  return (
    <div className="flex items-center gap-1.5">
      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
      <span className="text-sm font-bold font-mono" style={{ color }}>{risk}</span>
    </div>
  )
}

function ProgressMini({ done, total, mandatoryDone, mandatory }) {
  const pct = total > 0 ? (done / total) * 100 : 0
  const allMandatory = mandatoryDone >= mandatory
  return (
    <div className="flex items-center gap-1.5">
      <div className="w-16 h-1.5 rounded-full bg-gray-100 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-300"
          style={{ width: `${pct}%`, backgroundColor: allMandatory ? '#16a34a' : '#0A5169' }}
        />
      </div>
      <span className="text-xs font-mono text-gray-500">
        {done}/{total}
      </span>
    </div>
  )
}

function Tab({ label, count, active, onClick, color }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors ${
        active ? 'border-b-2 text-psg-teal border-psg-teal font-bold' : 'border-b-2 border-transparent text-gray-500'
      }`}
      style={active && color ? { borderColor: color, color } : {}}
    >
      {label}
      {count > 0 && (
        <span
          className={`text-xs font-bold px-2 py-0.5 rounded-full ${
            active ? 'bg-psg-teal/20 text-psg-teal' : 'bg-gray-100 text-gray-500'
          }`}
          style={active && color ? { backgroundColor: `${color}18`, color } : {}}
        >
          {count}
        </span>
      )}
    </button>
  )
}

function ECDDRow({ record, onOpen, venues }) {
  const navigate = useNavigate()
  const triggerConfig = ECDD_TRIGGER_CONFIG[record.triggerType] || ECDD_TRIGGER_CONFIG.manual
  const resolutionConfig = record.resolution ? ECDD_RESOLUTION_CONFIG[record.resolution] : null
  const venueName = venues.find((v) => v.id === record.venueId)?.shortName || record.venueId

  const handleClick = () => {
    onOpen(record.id)
    navigate(`/compliance-safe/ecdd/${record.id}`)
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClick() }}
      className="grid items-center gap-1.5 px-4 py-3 border-b border-gray-50 cursor-pointer transition-colors duration-150 hover:bg-psg-teal/5 border-l-[3px] border-l-transparent hover:border-l-psg-teal"
      style={{ gridTemplateColumns: '18px 86px 1.3fr 110px 80px 60px 86px 90px 56px' }}
    >
      <ChevronRightIcon className="w-3.5 h-3.5 text-gray-400" />
      <span className="text-xs font-mono text-gray-500">{record.id}</span>
      <div>
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-sm font-semibold text-psg-teal">{record.name}</span>
          <span className="text-xs font-mono text-gray-500">{record.pid}</span>
          {record.triggerType === 'pep' && (
            <Badge bg="#fef2f2" color="#dc2626" border="#fecaca">PEP</Badge>
          )}
        </div>
        <div className="text-sm text-gray-500 leading-tight">{record.trigger}</div>
      </div>
      <div>
        <StatusBadge status={record.status} />
        {resolutionConfig && (
          <div className="text-xs font-semibold mt-0.5" style={{ color: resolutionConfig.color }}>
            {resolutionConfig.label}
          </div>
        )}
      </div>
      <Badge bg={triggerConfig.bg} color={triggerConfig.color} border={triggerConfig.border}>
        {triggerConfig.label}
      </Badge>
      <RiskDot risk={record.risk} />
      <ProgressMini {...record.checklist} />
      <div>
        <div className="text-sm text-gray-600">{record.assignedTo}</div>
        {record.status === 'pending_approval' && record.manager && (
          <div className="text-xs text-amber-600">→ {record.manager}</div>
        )}
      </div>
      <div className="text-right">
        <div className={`text-sm font-semibold ${record.daysOpen > 14 ? 'text-orange-600' : 'text-gray-500'}`}>
          {record.daysOpen}d
        </div>
        {record.linkedSmr && (
          <span className="text-xs font-mono text-red-600">{record.linkedSmr}</span>
        )}
      </div>
    </div>
  )
}

export default function ECDDList() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const memberIdFromUrl = searchParams.get('memberId') || ''
  const yearFromUrl = searchParams.get('year') || ''
  const venues = useVenues()
  const [tab, setTab] = useState('open')
  const [search, setSearch] = useState('')
  const [venueFilter, setVenueFilter] = useState('all')
  const [yearFilter, setYearFilter] = useState(yearFromUrl || '')
  const [records, setRecords] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [patronScreeningToolOpen, setPatronScreeningToolOpen] = useState(false)

  useEffect(() => {
    if (yearFromUrl && yearFromUrl !== yearFilter) setYearFilter(yearFromUrl)
  }, [yearFromUrl])

  const setYear = useCallback((y) => {
    setYearFilter(y)
    const next = new URLSearchParams(searchParams)
    if (y) next.set('year', y)
    else next.delete('year')
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      setError(null)
      try {
        const params = new URLSearchParams()
        if (memberIdFromUrl) params.set('member_id', memberIdFromUrl)
        if (venueFilter !== 'all') params.set('venue_id', venueFilter)
        if (yearFilter) params.set('year', yearFilter)
        if (search.trim() && !memberIdFromUrl) params.set('search', search.trim())
        const r = await authFetch(getLocalApiUrl(`/api/ecdd?${params}`))
        const j = await r.json()
        if (cancelled) return
        if (!j.success) throw new Error(j.error || 'Failed to load ECDD')
        setRecords(j.data || [])
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load ECDD')
        setRecords([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [venueFilter, search, memberIdFromUrl, yearFilter])

  const handleExport = () => {
    const cols = [
      { header: 'ID', accessor: r => r.id },
      { header: 'Member', accessor: r => r.name || '' },
      { header: 'Member ID', accessor: r => r.pid || '' },
      { header: 'Trigger', accessor: r => r.trigger || '' },
      { header: 'Status', accessor: r => r.status || '' },
      { header: 'Assigned To', accessor: r => r.assignedTo || '' },
      { header: 'Days Open', accessor: r => r.daysOpen ?? '' },
    ]
    const suffix = yearFilter ? `-${yearFilter}` : ''
    exportToCsv(filtered, cols, `ecdd-registry${suffix}.csv`)
  }

  const counts = {
    all: records.length,
    open: records.filter((r) => r.status === 'open').length,
    pending_approval: records.filter((r) => r.status === 'pending_approval').length,
    closed: records.filter((r) => r.status === 'closed').length,
  }

  const filtered = useMemo(() => {
    if (tab === 'all') return records
    return records.filter((r) => r.status === tab)
  }, [records, tab])

  return (
    <div className="space-y-6">
      {memberIdFromUrl && (
        <div className="px-4 py-2 rounded-lg bg-psg-teal/10 border border-psg-teal/30 text-sm text-gray-700 flex items-center justify-between">
          <span>Showing ECDD for member <strong className="font-mono">{memberIdFromUrl}</strong></span>
          <Link to="/compliance-safe/ecdd" className="text-psg-teal hover:underline">View all</Link>
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-psg-teal">ECDD Management</h1>
          <p className="text-sm text-gray-500 mt-1">
            Enhanced Customer Due Diligence — open investigations and approvals
          </p>
        </div>
        <div className="flex flex-wrap gap-2 justify-end">
          {isPatronScreeningPrototypeEnabled() && (
          <Button
            type="button"
            variant="outline"
            className="border-psg-teal/40 text-psg-teal hover:bg-psg-teal/5"
            onClick={() => setPatronScreeningToolOpen(true)}
            title="Patron screening prototype (reference OSINT sources). Shown in dev by default; production needs VITE_ENABLE_PATRON_SCREENING_PROTOTYPE=true. Set to false to hide everywhere."
          >
            <MagnifyingGlassIcon className="w-4 h-4 mr-1.5" />
            Screening sources
          </Button>
          )}
          <Button
            type="button"
            className="bg-psg-teal hover:bg-psg-teal-700"
            onClick={() => navigate('/compliance-safe/investigations')}
            title="Start or link ECDD from an investigation"
          >
            <PlusIcon className="w-4 h-4 mr-1.5" />
            New ECDD
          </Button>
          <Button variant="outline" onClick={handleExport} disabled={loading || filtered.length === 0}>
            <ArrowDownTrayIcon className="w-4 h-4 mr-1.5" />
            Export
          </Button>
        </div>
      </div>

      {/* Tabs & Search */}
      <div className="flex items-center border-b border-gray-200">
        <Tab label="Open" count={counts.open} active={tab === 'open'} onClick={() => setTab('open')} />
        <Tab label="Pending Approval" count={counts.pending_approval} active={tab === 'pending_approval'} onClick={() => setTab('pending_approval')} color="#ca8a04" />
        <Tab label="Closed" count={counts.closed} active={tab === 'closed'} onClick={() => setTab('closed')} />
        <Tab label="All" count={counts.all} active={tab === 'all'} onClick={() => setTab('all')} />
        <div className="flex-1" />
        <select
          value={yearFilter}
          onChange={(e) => setYear(e.target.value)}
          className="mb-2 mr-2 pl-3 pr-8 py-1.5 rounded-lg text-sm font-medium border border-gray-200 bg-white focus:ring-2 focus:ring-psg-teal/20 cursor-pointer text-gray-600"
        >
          <option value="">All years</option>
          {getYearOptions().map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        <div className="relative mb-2 mr-2">
          <select
            value={venueFilter}
            onChange={(e) => setVenueFilter(e.target.value)}
            className="appearance-none pl-8 pr-8 py-1.5 rounded-lg text-sm font-medium border border-gray-200 bg-white focus:ring-2 focus:ring-psg-teal/20 cursor-pointer text-gray-600"
          >
            <option value="all">All Venues</option>
            {venues.map((v) => (
              <option key={v.id} value={v.id}>{v.name}</option>
            ))}
          </select>
          <div className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none">
            {venueFilter === 'all' ? <GlobeAltIcon className="w-4 h-4 text-gray-400" /> : <BuildingOffice2Icon className="w-4 h-4 text-gray-400" />}
          </div>
        </div>
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 bg-white mb-2">
          <MagnifyingGlassIcon className="w-4 h-4 text-gray-400" />
          <input
            placeholder="Search name, ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border-none outline-none text-sm bg-transparent w-44 text-gray-700"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
        <div
          className="grid gap-1.5 px-4 py-2.5 border-b border-gray-100 bg-gray-50/50"
          style={{ gridTemplateColumns: '18px 86px 1.3fr 110px 80px 60px 86px 90px 56px' }}
        >
          <span />
          {['ID', 'Subject / Trigger', 'Status', 'Type', 'Risk', 'Progress', 'Assigned', 'Days'].map((h) => (
            <span key={h} className="text-xs font-semibold uppercase tracking-wider text-gray-500">{h}</span>
          ))}
        </div>
        {error && <div className="px-4 py-3 text-sm text-red-600 bg-red-50">{error}</div>}
        {loading ? (
          <div className="py-12 text-center text-gray-500 text-sm">Loading…</div>
        ) : filtered.length > 0 ? (
          filtered.map((r) => <ECDDRow key={r.id} record={r} onOpen={() => {}} venues={venues} />)
        ) : (
          <div className="py-12 text-center text-gray-500 text-sm">
            {tab === 'pending_approval' ? 'No ECDDs pending approval' : 'No ECDDs match your filters'}
          </div>
        )}
        <div className="px-4 py-2.5 border-t border-gray-100 flex justify-between text-xs text-gray-500">
          <span>{filtered.length} ECDD records</span>
          <span>ECDD must never delay SMR submission — AML/CTF Act s81(3)</span>
        </div>
      </div>

      {/* Info Banner for Pending Approval */}
      {tab === 'pending_approval' && counts.pending_approval > 0 && (
        <div className="px-4 py-3 rounded-lg flex items-center gap-2.5 text-sm bg-amber-50 border border-amber-200 text-amber-800">
          <ClockIcon className="w-5 h-5 flex-shrink-0" />
          <span>
            <b>{counts.pending_approval} ECDD{counts.pending_approval > 1 ? 's' : ''}</b> awaiting manager review and approval. Click any row to review the investigation and approve or return.
          </span>
        </div>
      )}

      <PatronScreeningToolModal
        open={patronScreeningToolOpen}
        onClose={() => setPatronScreeningToolOpen(false)}
        memberName=""
      />
    </div>
  )
}
